import { useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import GroupsIcon from '@mui/icons-material/Groups';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { CustomUser } from 'api/models/CustomUser';
import { ApiService } from 'api/services/ApiService';
import { useUserGroups } from 'dashboard409a/hooks/useUserGroups';
import { UserGroupForm } from './UserGroupForm';
import { UserGroup } from './UserGroups.types';

interface UserGroupsModalProps {
  open: boolean;
  onClose: () => void;
  onGroupUpdated?: (updatedGroup: UserGroup) => void;
}

type ModalView = 'list' | 'form';

const EMPTY_FORM = { name: '', selectedUsers: [] as CustomUser[], isDefault: false, userSearchInput: '' };

const getFormTitle = (editingGroup: UserGroup | undefined) => editingGroup ? 'Edit Group' : 'New Group';

export const UserGroupsModal = ({ open, onClose, onGroupUpdated }: UserGroupsModalProps) => {
  const { groups, createGroup, updateGroup, deleteGroup } = useUserGroups();
  const [view, setView] = useState<ModalView>('list');
  const [editingGroup, setEditingGroup] = useState<UserGroup | undefined>(undefined);

  // Controlled form state lifted here so Save/Cancel live in DialogActions
  const [formName, setFormName] = useState('');
  const [formSelectedUsers, setFormSelectedUsers] = useState<CustomUser[]>([]);
  const [formIsDefault, setFormIsDefault] = useState(false);
  const [formUserSearchInput, setFormUserSearchInput] = useState('');

  const { data: staffUsers = [], isLoading: isLoadingStaff } = useQuery({
    queryKey: ['users-list', 'staff'],
    queryFn: async () => {
      const response = await ApiService.apiUsersStaffList();
      return (response.filter(u => !u.email?.includes('+')) as CustomUser[]) || [];
    },
    staleTime: 60 * 60 * 1000,
  });

  // Populate form when editing an existing group
  useEffect(() => {
    if (editingGroup && staffUsers.length > 0) {
      setFormName(editingGroup.name);
      setFormIsDefault(editingGroup.isDefault);
      const users = editingGroup.userIds
        .map(id => staffUsers.find(u => u.id === id))
        .filter((u): u is CustomUser => u !== undefined);
      setFormSelectedUsers(users);
    }
  }, [editingGroup, staffUsers]);

  const resetForm = () => {
    setFormName(EMPTY_FORM.name);
    setFormSelectedUsers(EMPTY_FORM.selectedUsers);
    setFormIsDefault(EMPTY_FORM.isDefault);
    setFormUserSearchInput(EMPTY_FORM.userSearchInput);
  };

  const handleCreateNew = () => {
    resetForm();
    setEditingGroup(undefined);
    if (groups.length === 0) {
      setFormIsDefault(true);
    }
    setView('form');
  };

  const handleEdit = (group: UserGroup) => {
    resetForm();
    setEditingGroup(group);
    setView('form');
  };

  const handleSave = async () => {
    if (!formName.trim()) return;
    const userIds = formSelectedUsers
      .map(u => u.id)
      .filter((id): id is number => id !== undefined);
    if (editingGroup) {
      const updatedGroup = updateGroup({
        ...editingGroup,
        name: formName.trim(),
        userIds,
        isDefault: formIsDefault,
      });
      onGroupUpdated?.(updatedGroup);
    } else {
      createGroup(formName.trim(), userIds, formIsDefault);
    }
    setView('list');
    setEditingGroup(undefined);
    resetForm();
  };

  const handleCancelForm = () => {
    setView('list');
    setEditingGroup(undefined);
    resetForm();
  };

  const handleClose = () => {
    setView('list');
    setEditingGroup(undefined);
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ py: 1.5 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <GroupsIcon fontSize="small" />
          <Typography variant="body1" fontWeight="medium" component="span">
            {view === 'list' ? 'User Groups' : getFormTitle(editingGroup)}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {view === 'list' ? (
          <Box>
            {groups.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                No groups yet. Create your first group.
              </Typography>
            ) : (
              <List disablePadding>
                {groups.map((group, index) => (
                  <Box key={group.id}>
                    {index > 0 && <Divider />}
                    <ListItem
                      dense
                      secondaryAction={
                        <Box display="flex" gap={0.5}>
                          <Tooltip title="Edit group">
                            <IconButton
                              size="small"
                              onClick={e => {
                                e.stopPropagation();
                                handleEdit(group);
                              }}
                            >
                              <EditIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete group">
                            <IconButton
                              size="small"
                              onClick={e => {
                                e.stopPropagation();
                                deleteGroup(group.id);
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                      sx={{ cursor: 'pointer', borderRadius: 1, py: 0.5, '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => handleEdit(group)}
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={0.75}>
                            <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8125rem' }}>
                              {group.name}
                            </Typography>
                            {group.isDefault && (
                              <Chip
                                label="Default"
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ height: 16, fontSize: '0.625rem' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={`${group.userIds.length} user${group.userIds.length === 1 ? '' : 's'}`}
                        slotProps={{ secondary: { sx: { fontSize: '0.75rem' } } }}
                      />
                    </ListItem>
                  </Box>
                ))}
              </List>
            )}
          </Box>
        ) : (
          <UserGroupForm
            name={formName}
            onNameChange={setFormName}
            selectedUsers={formSelectedUsers}
            onUsersChange={setFormSelectedUsers}
            userSearchInput={formUserSearchInput}
            onUserSearchInputChange={setFormUserSearchInput}
            isDefault={formIsDefault}
            onIsDefaultChange={setFormIsDefault}
            staffUsers={staffUsers}
            isLoadingStaff={isLoadingStaff}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ py: 1, px: 2 }}>
        {view === 'list' ? (
          <>
            <Button size="small" sx={{ fontSize: '0.75rem' }} onClick={handleClose}>Close</Button>
            <Button size="small" sx={{ fontSize: '0.75rem' }} variant="contained" startIcon={<AddIcon sx={{ fontSize: '0.875rem !important' }} />} onClick={handleCreateNew}>
              New Group
            </Button>
          </>
        ) : (
          <>
            <Button size="small" sx={{ fontSize: '0.75rem' }} onClick={handleCancelForm}>Cancel</Button>
            <Button
              size="small"
              sx={{ fontSize: '0.75rem' }}
              variant="contained"
              onClick={handleSave}
              disabled={!formName.trim()}
            >
              {editingGroup ? 'Update' : 'Create'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
